<script setup lang="ts">
import { computed, ref } from 'vue'
import type { SwingFundView } from '../../api/client'

// Spec 2026-10-17 §7: one swing-priced fund's card in NAV → Swing pricing. Pure view of the
// /api/swing-watch payload — it makes no request of its own.
const props = defineProps<{ view: SwingFundView; title: string }>()
const open = ref(false)
const r = computed(() => props.view.result)
const W = 340, H = 56, MID = H / 2, SC = (H / 2 - 4) / 3

const bp = (x: number | null) => x == null ? '—' : `${x > 0 ? '+' : x < 0 ? '−' : ''}${Math.abs(Math.round(x))}bp`
const since = computed(() => {
  const s = props.view.swing_priced_since
  if (!s) return 'swing-priced'
  const d = new Date(`${s}T00:00:00Z`)
  return `swing-priced since ${d.getUTCDate()} ${d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })} ${d.getUTCFullYear()}`
})
const bars = computed(() => {
  const rows = r.value?.rows ?? []
  const bw = W / Math.max(rows.length, 1)
  const todayUk = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' })
  return rows.map((row, i) => {
    const z = Math.max(-3, Math.min(3, row.z ?? 0)); const y = MID - z * SC
    const cls = row.unusual && row.deal ? 'unusual-deal' : row.unusual ? 'unusual' : row.date === todayUk ? 'today' : 'normal'
    return { x: i * bw + 0.5, y: Math.min(y, MID), w: Math.max(bw - 1, 1), h: Math.max(Math.abs(y - MID), 1), cls, row, cx: i * bw + bw / 2 }
  })
})
const unusual = computed(() => (r.value?.rows ?? []).filter(x => x.unusual).slice().reverse())
const pillAmber = computed(() => (r.value?.unusual_count ?? 0) > 0)
</script>

<template>
  <section class="swing-card" :data-testid="`swing-card-${view.fund.toLowerCase()}`">
    <header class="sw-head">
      <b>{{ title }}</b>
      <span v-if="r" class="sw-pill" :class="{ amber: pillAmber }" data-testid="swing-pill">{{ r.unusual_count }}/{{ r.window_days }} · {{ r.unusual_on_deals }} on our deals</span>
    </header>
    <div class="sw-since">{{ since }}</div>
    <div v-if="!r" class="sw-error">couldn't load: {{ view.error }}</div>
    <template v-else>
      <svg :viewBox="`0 0 ${W} ${H + 12}`" class="sw-strip" role="img" :aria-label="`${title} daily gap strip`">
        <line :x1="0" :x2="W" :y1="MID" :y2="MID" class="axis" />
        <g v-if="r.band_bp != null" data-testid="swing-band">
          <line :x1="0" :x2="W" :y1="MID - 2 * SC" :y2="MID - 2 * SC" class="band" />
          <line :x1="0" :x2="W" :y1="MID + 2 * SC" :y2="MID + 2 * SC" class="band" />
          <text :x="W - 2" :y="MID - 2 * SC - 2" text-anchor="end" class="lbl">±{{ Math.round(r.band_bp) }}bp</text>
        </g>
        <rect v-for="b in bars" :key="b.row.date" :x="b.x" :y="b.y" :width="b.w" :height="b.h" :class="b.cls" data-testid="swing-bar">
          <title>{{ b.row.date }}: {{ bp(b.row.gap_bp) }}</title>
        </rect>
        <template v-for="b in bars" :key="`t-${b.row.date}`">
          <text v-if="b.row.deal === 'BUY'" :x="b.cx" :y="H + 10" text-anchor="middle" class="tick buy">▲</text>
          <text v-else-if="b.row.deal === 'SELL'" :x="b.cx" :y="H + 10" text-anchor="middle" class="tick sell">▼</text>
        </template>
      </svg>
      <div class="sw-means">
        <span>BUY {{ bp(r.buy_mean_bp) }} <i>n={{ r.buy_count }}</i></span>
        <span>SELL {{ bp(r.sell_mean_bp) }} <i>n={{ r.sell_count }}</i></span>
        <span>other {{ bp(r.other_mean_bp) }}</span>
      </div>
      <div class="sw-foot">
        <span class="sw-verdict" :class="{ amber: r.verdict === 'swing showing on our deals' }">Verdict: {{ r.verdict }}</span>
        <button class="sw-toggle" data-testid="swing-details-toggle" @click="open = !open">Details {{ open ? '▴' : '▾' }}</button>
      </div>
      <div v-if="open" data-testid="swing-details" class="sw-details">
        <div class="sw-sub">Unusual days (beyond ±2 sd), newest first</div>
        <table><tbody>
          <tr class="hd"><td>Day</td><td>Gap</td><td>Next day</td><td>Our deal</td></tr>
          <tr v-for="u in unusual" :key="u.date" data-testid="swing-unusual-row">
            <td>{{ u.date.slice(5) }}</td><td>{{ bp(u.gap_bp) }}</td>
            <td>{{ u.next_gap_bp == null ? 'pending' : `${bp(u.next_gap_bp)} (${Math.round(u.given_back_pct ?? 0)}% back)` }}</td>
            <td>{{ u.deal ?? '—' }}</td>
          </tr>
        </tbody></table>
        <p class="sw-note">How to read it: a swing shows as a gap in the SAME direction as our deal on our deal days (we BUY → NAV swung up → positive gap), larger than on the fund's other days. A gap that simply reverses next day is the stale-NAV pattern, not swing.</p>
      </div>
    </template>
  </section>
</template>

<style scoped>
.swing-card { box-sizing: border-box; max-width: 100%; overflow-wrap: anywhere; background: var(--card-bg, #1c2128); border: 1px solid var(--border, #30363d); border-radius: 8px; padding: 8px 10px; margin: 6px 0; font-size: 12px; }
.sw-head { display: flex; justify-content: space-between; align-items: center; gap: 6px; }
.sw-pill, .sw-verdict { border-radius: 10px; padding: 1px 7px; font-size: 11px; background: #2d333b; }
.amber { background: #3d2f12; color: #e5a83b; }
.sw-since, .sw-sub, .sw-note { opacity: .7; font-size: 11px; }
.sw-strip { width: 100%; height: auto; display: block; margin: 4px 0; }
.axis { stroke: #555; } .band { stroke: #777; stroke-dasharray: 3 3; } .lbl { font-size: 9px; fill: #999; }
.normal { fill: #5b6b7f; } .unusual { fill: #e5a83b; } .unusual-deal { fill: #e0533d; } .today { fill: #58a6ff; }
.tick { font-size: 8px; } .buy { fill: #3fb950; } .sell { fill: #f85149; }
.sw-means { display: flex; gap: 10px; flex-wrap: wrap; } .sw-means i { opacity: .6; font-style: normal; }
.sw-foot { display: flex; justify-content: space-between; align-items: center; margin-top: 4px; gap: 6px; }
.sw-toggle { background: none; border: 0; color: #58a6ff; cursor: pointer; font-size: 12px; }
.sw-details { overflow-x: hidden; } .sw-details table { width: 100%; table-layout: fixed; font-size: 11px; border-collapse: collapse; } .hd { opacity: .7; }
.sw-error { color: #e5a83b; }
</style>
